import { useEffect } from "react";
import { useI18n } from "../i18n/LanguageProvider";
import { LOCALES, SITE_URL } from "../i18n/config";

const OG_LOCALE = {
  es: "es_ES", en: "en_US", fr: "fr_FR", de: "de_DE", it: "it_IT", pt: "pt_PT",
  nl: "nl_NL", pl: "pl_PL", ro: "ro_RO", sv: "sv_SE", da: "da_DK", no: "nb_NO",
};

const OG_IMAGE = `${SITE_URL}/og-image.png?v=2`;
const LOGO = `${SITE_URL}/centauros-isotype.png`;

function upsert(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function setMeta(attr, key, content) {
  const el = upsert(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute(attr, key);
    return m;
  });
  el.setAttribute("content", content);
}

function removeAll(selector) {
  document.head.querySelectorAll(selector).forEach((n) => n.remove());
}

/**
 * Head/SEO manager. Sets title, description, canonical, OpenGraph, Twitter,
 * hreflang alternates and JSON-LD structured data for every locale.
 *
 * Props:
 *  - titleKey / descKey: i18n keys
 *  - path: route after the locale (e.g. "/contact")
 *  - pageType: "home" | "contact" | "privacy" | "terms" | "cookies" | "support" | "page"
 *  - breadcrumbs: [{ name, path }] — path relative to locale ("" = home)
 */
export default function Seo({ titleKey, descKey, path = "", pageType = "page", breadcrumbs = null }) {
  const { t, locale } = useI18n();
  const title = t(titleKey);
  const description = t(descKey);
  const url = `${SITE_URL}/${locale}${path}`;

  useEffect(() => {
    document.documentElement.lang = locale;
    document.title = title;

    setMeta("name", "description", description);

    // Open Graph
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "Centauros");
    setMeta("property", "og:locale", OG_LOCALE[locale] || "es_ES");
    setMeta("property", "og:image", OG_IMAGE);
    setMeta("property", "og:image:secure_url", OG_IMAGE);
    setMeta("property", "og:image:type", "image/png");
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("property", "og:image:alt", "Centauros");
    setMeta("property", "og:url", url);

    // og:locale:alternate for every other locale
    removeAll('meta[data-oglocale="1"]');
    LOCALES.forEach(({ code }) => {
      if (code === locale) return;
      const m = document.createElement("meta");
      m.setAttribute("property", "og:locale:alternate");
      m.setAttribute("content", OG_LOCALE[code] || code);
      m.setAttribute("data-oglocale", "1");
      document.head.appendChild(m);
    });

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", OG_IMAGE);
    setMeta("name", "twitter:image:alt", "Centauros");

    // Canonical
    const canonical = upsert('link[rel="canonical"]', () => {
      const l = document.createElement("link");
      l.setAttribute("rel", "canonical");
      return l;
    });
    canonical.setAttribute("href", url);

    // hreflang alternates
    removeAll('link[data-hreflang="1"]');
    LOCALES.forEach(({ code }) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", code);
      link.setAttribute("href", `${SITE_URL}/${code}${path}`);
      link.setAttribute("data-hreflang", "1");
      document.head.appendChild(link);
    });
    const xdef = document.createElement("link");
    xdef.setAttribute("rel", "alternate");
    xdef.setAttribute("hreflang", "x-default");
    xdef.setAttribute("href", `${SITE_URL}/es${path}`);
    xdef.setAttribute("data-hreflang", "1");
    document.head.appendChild(xdef);

    // ---------- JSON-LD structured data ----------
    const organization = {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "Centauros",
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: LOGO, width: 512, height: 512 },
      image: OG_IMAGE,
      description: t("meta.home.description"),
      email: "support@centauros.app",
      contactPoint: {
        "@type": "ContactPoint",
        email: "support@centauros.app",
        contactType: "customer support",
        availableLanguage: LOCALES.map((l) => l.code),
      },
    };

    const website = {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: SITE_URL,
      name: "Centauros",
      description: t("meta.home.description"),
      publisher: { "@id": `${SITE_URL}/#organization` },
      inLanguage: locale,
    };

    const softwareApp = {
      "@type": "SoftwareApplication",
      "@id": `${SITE_URL}/#software`,
      name: "Centauros",
      operatingSystem: "iOS, Android",
      applicationCategory: "FinanceApplication",
      url: SITE_URL,
      image: OG_IMAGE,
      description: t("meta.home.description"),
      inLanguage: locale,
      publisher: { "@id": `${SITE_URL}/#organization` },
      offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
    };

    const webPageType =
      pageType === "contact" ? "ContactPage" : pageType === "home" ? "WebPage" : "WebPage";
    const webPage = {
      "@type": webPageType,
      "@id": `${url}#webpage`,
      url,
      name: title,
      description,
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": `${SITE_URL}/#organization` },
      inLanguage: locale,
      primaryImageOfPage: { "@type": "ImageObject", url: OG_IMAGE },
    };

    const graph = [organization, website, webPage];
    if (pageType === "home") graph.push(softwareApp);

    // BreadcrumbList
    if (breadcrumbs && breadcrumbs.length) {
      graph.push({
        "@type": "BreadcrumbList",
        "@id": `${url}#breadcrumb`,
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: `${SITE_URL}/${locale}${b.path || ""}`,
        })),
      });
    }

    // FAQPage (home) — pulled from i18n faqx
    if (pageType === "home") {
      const faqx = t("faqx");
      if (Array.isArray(faqx) && faqx.length) {
        graph.push({
          "@type": "FAQPage",
          "@id": `${url}#faq`,
          mainEntity: faqx.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        });
      }
    }

    const ld = { "@context": "https://schema.org", "@graph": graph };
    removeAll('script[data-ld="1"]');
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-ld", "1");
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);
  }, [title, description, locale, path, pageType, url]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
